import { memo, useMemo, useState } from "react";
import { SmilePlus, Trash2 } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useAuthor } from "@/hooks/useAuthor";
import { genUserName } from "@/lib/genUserName";
import { cn } from "@/lib/utils";
import type { ChatMessage } from "@/lib/concord/types";

const URL_RE = /(https?:\/\/[^\s<>"']+)/g;
const URL_TEST = /^https?:\/\//;
const QUICK_EMOJI = ["👍", "❤️", "🔥", "😂", "🙏", "🎉"];

/** https-only allowlist: anything else renders as plain text. */
function sanitizeUrl(raw: string): string | null {
  try {
    const u = new URL(raw);
    return u.protocol === "https:" ? u.href : null;
  } catch {
    return null;
  }
}

function MessageText({ content }: { content: string }) {
  const parts = useMemo(() => content.split(URL_RE), [content]);
  return (
    <p className="whitespace-pre-wrap break-words leading-relaxed">
      {parts.map((part, i) => {
        if (!URL_TEST.test(part)) return <span key={i}>{part}</span>;
        const href = sanitizeUrl(part);
        if (!href) return <span key={i}>{part}</span>;
        return (
          <a
            key={i}
            href={href}
            target="_blank"
            rel="noreferrer noopener"
            className="text-primary underline underline-offset-2 hover:text-primary/80 break-all"
          >
            {part}
          </a>
        );
      })}
    </p>
  );
}

function formatTime(ms: number): string {
  const d = new Date(ms);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const time = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  if (sameDay) return time;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return `Yesterday ${time}`;
  return `${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })} ${time}`;
}

interface MessageItemProps {
  message: ChatMessage;
  grouped: boolean;
  isMine: boolean;
  onReact: (emoji: string) => void;
  onDelete: () => void;
}

export const MessageItem = memo(function MessageItem({
  message,
  grouped,
  isMine,
  onReact,
  onDelete,
}: MessageItemProps) {
  const author = useAuthor(message.pubkey);
  const metadata = author.data?.metadata;
  const displayName = metadata?.name ?? metadata?.display_name ?? genUserName(message.pubkey);
  const [pickerOpen, setPickerOpen] = useState(false);

  return (
    <div
      className={cn(
        "group relative flex gap-3 px-4 py-1 hover:bg-secondary/50",
        grouped ? "mt-0" : "mt-4",
      )}
    >
      <div className="w-10 shrink-0">
        {!grouped && (
          <Avatar className="size-10 border">
            <AvatarImage src={metadata?.picture} alt={displayName} />
            <AvatarFallback className="bg-primary/10 text-primary text-sm font-semibold">
              {displayName.slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
        )}
      </div>
      <div className="min-w-0 flex-1">
        {!grouped && (
          <div className="flex items-baseline gap-2">
            <span className={cn("font-semibold text-sm", isMine && "text-primary")}>
              {displayName}
            </span>
            <span className="text-xs text-muted-foreground">
              {formatTime(message.createdAtMs)}
            </span>
          </div>
        )}
        <div className="text-[0.95rem]">
          <MessageText content={message.content} />
          {message.edited && (
            <span className="text-xs text-muted-foreground">(edited)</span>
          )}
        </div>
        {Object.keys(message.reactions).length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {Object.entries(message.reactions).map(([emoji, r]) => (
              <button
                key={emoji}
                type="button"
                onClick={() => onReact(emoji)}
                className={cn(
                  "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs transition-colors",
                  r.mine
                    ? "border-primary/50 bg-primary/10 text-primary"
                    : "bg-card hover:bg-secondary",
                )}
              >
                <span>{emoji}</span>
                <span className="tabular-nums">{r.count}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Hover actions */}
      <div className="absolute -top-3 right-4 hidden group-hover:flex items-center gap-1 rounded-lg border bg-card p-1 shadow-sm">
        <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="icon" className="size-7" aria-label="Add reaction">
              <SmilePlus className="size-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-2" align="end">
            <div className="flex gap-1">
              {QUICK_EMOJI.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  className="rounded-md p-1.5 text-lg hover:bg-secondary"
                  onClick={() => {
                    onReact(emoji);
                    setPickerOpen(false);
                  }}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
        {isMine && (
          <Button
            variant="ghost"
            size="icon"
            className="size-7 text-destructive hover:text-destructive"
            aria-label="Delete message"
            onClick={onDelete}
          >
            <Trash2 className="size-4" />
          </Button>
        )}
      </div>
    </div>
  );
});
